import axios from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { CustomerIdInput } from '@/lib/services/customers/customers.types'
import { conditionalObjectMerge } from '@/lib/utils'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  CreateMessageCustomerInput,
  CreateMessageInput,
  Message,
  MessageIdInput,
  MessageIndexFilters,
  MessageIndexResponse,
  UpdateMessageInput,
} from './messages.types'

export interface MessageOrganizationScope {
  organizationId: OrganizationId
}

/** Scope for calls that act on one message; the Laravel routes require its id. */
export interface MessageScope extends MessageOrganizationScope {
  messageId: MessageIdInput
}

/** The show query key's entries; the shared key builder can't mark an entry required. */
export interface MessageShowKeyEntries extends MessageOrganizationScope {
  messageId?: MessageIdInput
}

/** Query functions read only the key (and the abort signal when the caller passes one). */
type MessageQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'> &
  Partial<Pick<QueryFunctionContext<[TKey]>, 'signal'>>

export type MessageIndexQueryKeyEntries = MessageOrganizationScope & {
  filters?: MessageIndexFilters | undefined
  page?: number | undefined
  pageSize?: number | undefined
}

export async function getMessages({
  queryKey: [{ organizationId, filters = {}, page = 1, pageSize = 20 }],
  signal,
}: MessageQueryContext<MessageIndexQueryKeyEntries>) {
  const params = conditionalObjectMerge(
    {
      ...filters,
      page,
      page_size: pageSize,
    },
    {}
  )

  return (
    (await axios.get<MessageIndexResponse>(
      '/messages',
      buildAdminRequestConfig({
        organizationId,
        params,
        ...(signal ? { signal } : {}),
      })
    )) || {
      data: [],
      meta: {
        current_page: page,
        page_size: pageSize,
        total: 0,
        last_page: 1,
      },
    }
  )
}

export async function getMessage({
  queryKey: [{ organizationId, messageId }],
}: MessageQueryContext<MessageShowKeyEntries>) {
  const response = await axios.get<Message>(
    `/messages/${messageId}`,
    buildAdminRequestConfig({ organizationId })
  )
  return response || {}
}

export async function createMessage({
  organizationId,
  ...values
}: MessageOrganizationScope & CreateMessageInput) {
  return axios.post<Message>('/messages', values, buildAdminRequestConfig({ organizationId }))
}

export async function updateMessage({
  organizationId,
  messageId,
  ...values
}: MessageScope & UpdateMessageInput) {
  return axios.put<Message>(
    `/messages/${messageId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function markMessageRead({ organizationId, messageId }: MessageScope) {
  return axios.put<Message>(
    `/messages/${messageId}/read`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function markMessageUnread({ organizationId, messageId }: MessageScope) {
  return axios.put<Message>(
    `/messages/${messageId}/unread`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

/** `customer_id` is `present`; null unlinks the message's customer. */
export async function linkMessageCustomer({
  organizationId,
  messageId,
  customerId,
}: MessageScope & { customerId: CustomerIdInput | null }) {
  return axios.put<Message>(
    `/messages/${messageId}/customer`,
    { customer_id: customerId },
    buildAdminRequestConfig({ organizationId })
  )
}

/** Creates a customer from the request fields and links it to the message. */
export async function createMessageCustomer({
  organizationId,
  messageId,
  ...values
}: MessageScope & CreateMessageCustomerInput) {
  return axios.post<Message>(
    `/messages/${messageId}/customer`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

/** 204 No Content: resolves to the empty body. */
export async function softDeleteMessage({ organizationId, messageId }: MessageScope) {
  return axios.delete<string>(`/messages/${messageId}`, buildAdminRequestConfig({ organizationId }))
}
